import _ from 'lodash';
import Vue from 'vue';
import i18n from '../i18n';
import { DpSentryInit } from '@docupet/component-library';
import { upperFirst, camelCase, forEach } from 'lodash';
import { FontAwesomeIcon, FontAwesomeLayers } from '@/components/config/fontawesome';

import { componentsPlugin, installFactory } from '@/components/config/bootstrap';

import { ValidationObserver } from 'vee-validate';
import { ValidationProvider, extend } from 'vee-validate/dist/vee-validate.full.esm';

const env = (process.env?.VUE_APP_ENV || process.env?.NODE_ENV)?.toLowerCase();
if (env !== 'local') {
  DpSentryInit(Vue, 'https://a522ab9e59824683acab570bda2601c8@o452146.ingest.sentry.io/5816378', env);
}

// Bootstrap Tree-shaking
const BootstrapVue = installFactory(
  {
    plugins: {
      componentsPlugin,
    },
  },
  'BootstrapVue'
);

Vue.use(BootstrapVue);

Vue.component('FontAwesomeIcon', FontAwesomeIcon);
Vue.component('font-awesome-layers', FontAwesomeLayers);
Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);

Vue.set(Vue.prototype, '_', _);

const requireComponent = require.context('.', false, /Base[A-Z]\w+\.(vue|js)$/);

requireComponent.keys().forEach((fileName) => {
  const componentConfig = requireComponent(fileName);

  const componentName = upperFirst(camelCase(fileName.replace(/^\.\/(.*)\.\w+$/, '$1')));

  Vue.component(componentName, componentConfig.default || componentConfig);
});

//CUSTOM REGEX VALIDATORS
const REGEXP_VALIDATORS = {
  oneLowerChar: /(?=.*[a-z])/,
  oneUpperChar: /(?=.*[A-Z])/,
  oneDigitChar: /(?=.*[0-9])/,
  oneSpecialChar: /(?=.*\W)/,
  phoneNumber: /\+?([2-9])?[ .-]?(\(?\d{3}\)?)[ .-]?(\d{3})[ .-]?(\d{4})/,
  allowedSpecialChar: /^[\w\s\-']+$/,
};

//DEFAULT RULES
forEach(REGEXP_VALIDATORS, (validation_exp, validation_key) => {
  extend(validation_key, {
    validate: (value) => validation_exp.test(value),
    message: (field) => `The ${field} is not valid`,
  });
});

//PASSWORDS MATCH
extend('passwordMatch', {
  params: ['target'],
  validate: (value, { target }) => value === target,
  message: i18n.t('errorMessages.passwordConfirmationDoesntMatch'),
});

//UNIT REGEX
let unitRegEx = /^[.a-zA-Z0-9,\s]+$/;
extend('unit', {
  params: ['target'],
  validate: (value) => unitRegEx.test(value),
  message: i18n.t('dp.input.unit.customErrorMessage'),
});

//EMAIL EXISTS
const emailExistsParams = ['email', 'message'];
extend(
  'emailExists',
  {
    validate: (value, params) => value !== params[0],
    message: (value, params) => params[1],
  },
  emailExistsParams
);

//PET NAME EXISTS
extend('petNameExists', {
  params: ['existingPetNames'],
  validate: (value, { existingPetNames }) => !existingPetNames.map((name) => name.toLowerCase()).includes(value.toLowerCase()),
  message: 'You can only have one pet with the same name! Please enter a unique pet name.',
});

extend('inactivePetNames', {
  params: ['inactivePetNames', 'customerSupportPhone'],
  validate: (value, { inactivePetNames }) => !inactivePetNames.map((name) => name.toLowerCase()).includes(value.toLowerCase()),
  message: (value, params) =>
    `A previously removed pet with this same name is associated with your account. If you would like to use this name for a new pet, get in touch with our customer care team at ${params.customerSupportPhone} or via email at info@docupet.com.`,
});

//PET DUPLICATE CHECK
extend('duplicatePet', {
  params: ['name', 'message'],
  validate: (value, { name }) => value.toLowerCase() !== name.toLowerCase(),
  message: (value, { message }) => message,
});

//OVERRIDE REQUIRED FIELDS WITH CUSTOM MESSAGES
const getCustomMessages = (field) => {
  let message = '';

  switch (true) {
    case field.startsWith('address-civic'):
      return i18n.t('dp.input.civic.customErrorMessage');
    case field.startsWith('address-street'):
      return i18n.t('dp.input.street.customErrorMessage');
    case field.startsWith('address-city'):
      return i18n.t('dp.input.city.customErrorMessage');
    case field.startsWith('address-state'):
      return i18n.t('dp.input.state.customErrorMessage');
    case field.startsWith('address-province'):
      return i18n.t('dp.input.province.customErrorMessage');
    case field.startsWith('address-postal-code'):
      return i18n.t('dp.input.postalCode.customErrorMessage.required');
    case field.startsWith('address-zip-code'):
      return i18n.t('dp.input.zipCode.customErrorMessage.required');
  }

  switch (field) {
    case 'first-name':
      message = i18n.t('dp.input.firstName.customErrorMessage.isEmpty');
      break;
    case 'last-name':
      message = i18n.t('dp.input.lastName.customErrorMessage.isEmpty');
      break;
    case 'email':
      message = i18n.t('dp.input.email.customErrorMessage.isEmpty');
      break;
    case 'password':
      message = i18n.t('dp.input.password.customErrorMessage.isEmpty');
      break;
    case 'accepted-tc-pp':
      message = i18n.t('dp.input.agreeToTermsConditions.customErrorMessage');
      break;
    case 'pet-species':
      message = i18n.t('petPanel.petSpeciesRequired');
      break;
    case 'pet-name':
      message = i18n.t('petPanel.petNameRequired');
      break;
    case 'pet-breed':
      message = i18n.t('petPanel.petBreedRequired');
      break;
    case 'pet-sex':
      message = i18n.t('petPanel.petSexRequired');
      break;
    case 'pet-age':
      message = i18n.t('petPanel.petAgeRequired');
      break;
    case 'birthdate-approximate':
      message = i18n.t('petPanel.petApproximateBirthdateRequired');
      break;
    case 'color-picker':
      message = i18n.t('petPanel.petColorRequired');
      break;
    case 'address-textarea':
      message = i18n.t('dp.input.addressSearch.customErrorMessage');
      break;
    case 'phone-number':
      message = i18n.t('input.phoneNumber.customErrorMessage.isEmpty');
      break;
    case 'month':
      message = i18n.t('dp.input.birthday.customErrorMessage.month.isEmpty');
      break;
    case 'day':
      message = i18n.t('dp.input.birthday.customErrorMessage.day.isEmpty');
      break;
    case 'year':
      message = i18n.t('dp.input.birthday.customErrorMessage.year.isEmpty');
      break;
    case 'terms-of-service-and-privacy-policy':
      message = i18n.t('errorMessages.termsOfServiceRequired');
      break;
    default:
      message = i18n.t('errorMessages.defaultRequired', { field: field.split('-').join(' ') });
  }
  return message;
};
extend('required', {
  message: getCustomMessages,
});

//OVERRIDE EMAIL FIELD MESSAGE
extend('email', {
  message: i18n.t('dp.input.email.customErrorMessage.hasValue'),
});

//OVERRIDE PHONE NUMBER FIELD MESSAGE
extend('phoneNumber', {
  message: i18n.t('input.phoneNumber.customErrorMessage.hasValue'),
  validate: (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    return /^[2-9]\d{9}$/.test(digitsOnly);
  },
});

//OVERRIDE BETWEEN MESSAGE
extend('between', {
  message: (value, params) => i18n.t('dp.input.dates.errors.between', { minNum: params.min, maxNum: params.max }),
});

//OVERRIDE MIN/MAX MESSAGES
extend('min', {
  message: (value, params) => i18n.t('dp.input.dates.errors.min', { numLimit: params.length, field: params._field_ }),
});
extend('max', {
  message: (value, params) => i18n.t('dp.input.dates.errors.max', { numLimit: params.length, field: params._field_ }),
});

extend('allowedSpecialChar', {
  message: (value, params) => i18n.t('errorMessages.invalidCharacters', { field: params._field_.split('-').join(' ') }),
});

//CUSTOM MINMAX NUMERIC MESSAGE
extend('minMaxNumeric', {
  params: ['min', 'max'],
  validate: (value, params) => {
    const { min, max } = params;
    const valueStringLength = value?.toString().length;
    if (valueStringLength < Number(min) || (valueStringLength === Number(min) && isNaN(value))) {
      return i18n.t('dp.input.dates.errors.minNumeric', { numLimit: min });
    }
    if (valueStringLength > Number(max) || (valueStringLength === Number(max) && isNaN(value))) {
      return i18n.t('dp.input.dates.errors.maxNumeric', { numLimit: max });
    }
    return true;
  },
});

const zipCodeRegEx = /^(\d{5})?$/;
extend('zipCode', {
  params: ['target'],
  validate: (value) => zipCodeRegEx.test(value),
  message: i18n.t('dp.input.zipCode.customErrorMessage.invalid'),
});

const postalCodeRegEx = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
extend('postalCode', {
  params: ['target'],
  validate: (value) => postalCodeRegEx.test(value),
  message: i18n.t('dp.input.postalCode.customErrorMessage.invalid'),
});
